import React, { useState } from 'react';
import { BanIcon, MinusIcon, PlusIcon } from 'lucide-react';
import type { LeadSource, SourceCapacity } from '../data/leadDistribution';
interface LeadSourcesCellProps {
  capacities: SourceCapacity[];
  sources: LeadSource[];
  officerId: string;
  onAddSource: (sourceId: LeadSource['id']) => void;
  onAdjustCap: (sourceId: LeadSource['id'], delta: number) => void;
}
export function LeadSourcesCell({
  capacities,
  sources,
  officerId,
  onAddSource,
  onAdjustCap
}: LeadSourcesCellProps) {
  const [selectedSourceId, setSelectedSourceId] = useState<LeadSource['id'] | ''>('');
  const availableSources = sources.map(source => ({
    source,
    capacity: capacities.find(capacity => capacity.sourceId === source.id)
  })).filter((item): item is {
    source: LeadSource;
    capacity: SourceCapacity;
  } => Boolean(item.capacity?.eligible));
  const addableSources = sources.filter(source => !capacities.some(capacity => capacity.sourceId === source.id && capacity.eligible));
  const addSelectedSource = () => {
    if (!selectedSourceId) return;
    onAddSource(selectedSourceId);
    setSelectedSourceId('');
  };
  return <td className="min-w-[350px] px-4 py-3.5 align-middle" data-id="element-59" data-mp="3pcxo2whqkfub">
      {availableSources.length === 0 ? <div className="mb-2 flex items-center gap-1.5 text-slate-300" data-id="element-60" data-mp="3bdy0ls5ym6zq">
          <BanIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-61" data-mp="3ffb621554864" />
          <span className="text-xs font-medium" data-id="element-62" data-mp="31oo9ofmuqud7">No eligible sources</span>
        </div> : <div className="flex flex-wrap gap-2" data-id="element-63" data-mp="36fedhofmqp5s">
          {availableSources.map(({
        source,
        capacity
      }) => {
        const remaining = Math.max(capacity.cap - capacity.assigned, 0);
        const full = remaining === 0;
        return <div key={source.id} className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5" data-id="element-64" data-mp="2wqd1sag4atxm">
                <div className="min-w-[72px]" data-id="element-65" data-mp="2irwsqcus8rcb">
                  <p className="truncate text-[11px] font-bold text-slate-600" title={source.name} data-id="element-66" data-mp="2np6gzp6fqwyk">{source.name}</p>
                  <p className={`text-[11px] font-bold tabular-nums ${full ? 'text-red-600' : 'text-rcm-600'}`} data-id="element-67" data-mp="t9gh51v71arh">
                    {remaining} <span className="font-medium text-slate-400" data-id="element-68" data-mp="ofvegwinnsly">left</span>
                  </p>
                </div>
                <div className="flex items-center gap-0.5" data-id="element-69" data-mp="jgiwap2qb9pp">
                  <button type="button" onClick={() => onAdjustCap(source.id, -1)} disabled={capacity.cap <= capacity.assigned} aria-label={`Lower ${source.name} total capacity`} className="flex h-6 w-6 items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:border-rcm-200 hover:bg-rcm-50 hover:text-rcm-700 disabled:cursor-not-allowed disabled:opacity-40" data-id="element-70" data-mp="fiuv54ag0kpd">
                    <MinusIcon className="h-3 w-3" data-id="element-71" data-mp="3nyem1wnn5r19" />
                  </button>
                  <button type="button" onClick={() => onAdjustCap(source.id, 1)} aria-label={`Raise ${source.name} total capacity`} className="flex h-6 w-6 items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:border-rcm-200 hover:bg-rcm-50 hover:text-rcm-700" data-id="element-72" data-mp="3ix7d3u9rilt0">
                    <PlusIcon className="h-3 w-3" data-id="element-73" data-mp="2c85y9nxcyr2z" />
                  </button>
                </div>
              </div>;
      })}
        </div>}

      {addableSources.length > 0 && <div className="mt-2 flex items-center gap-2" data-id="element-74" data-mp="2m493j2nib5u5">
          <label className="sr-only" htmlFor={`add-lead-source-${officerId}`} data-id="element-75" data-mp="2r4wrffgfv6k5">Add a lead source</label>
          <select id={`add-lead-source-${officerId}`} value={selectedSourceId} onChange={event => setSelectedSourceId(event.target.value as LeadSource['id'] | '')} className="h-7 min-w-[150px] rounded border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-600 focus:border-rcm-500 focus:outline-none focus:ring-1 focus:ring-rcm-500" data-id="element-76" data-mp="2v6qnfafovmdz">
            <option value="" data-id="element-77" data-mp="1h7c61olptwy">Add lead source…</option>
            {addableSources.map(source => <option key={source.id} value={source.id} data-id="element-78" data-mp="fgqjis9l9187">{source.name}</option>)}
          </select>
          <button type="button" onClick={addSelectedSource} disabled={!selectedSourceId} className="inline-flex h-7 items-center gap-1 rounded bg-rcm-50 px-2 text-xs font-bold text-rcm-700 hover:bg-rcm-100 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-rcm-500" data-id="element-79" data-mp="ahdnbgwtuhsy">
            <PlusIcon className="h-3.5 w-3.5" data-id="element-80" data-mp="3nw0hppacxmap" />
            Add
          </button>
        </div>}
    </td>;
}